import { TextSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { Box } from '@mui/material'
import React from 'react'
import { useInstallationStatusContext } from '../context/installation-status-context'
import { useCanViewInstallationDeviceStatus } from '../hooks/use-can-view-installation-device-status'
import BatteryStatusIcon from './device-status-icons/BatteryStatusIcon'
import GridStatusIcon from './device-status-icons/GridStatusIcon'
import OfflineStatusIcon from './device-status-icons/OfflineStatusIcon'
import PvStatusIcon from './device-status-icons/PvStatusIcon'
import WallboxStatusIcon from './device-status-icons/WallboxStatusIcon'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

interface InstallationDeviceStatusRowProps {
  installation: Installation
  placeHolderUi: string
}

const InstallationDeviceStatusRow: React.FC<InstallationDeviceStatusRowProps> = ({ installation, placeHolderUi }) => {
  const { canViewInstallationDeviceStatus } = useCanViewInstallationDeviceStatus(installation.installationStatus)

  if (!canViewInstallationDeviceStatus) return null

  return <InstallationDeviceStatusData installationId={installation.id} placeHolderUi={placeHolderUi} />
}

export default InstallationDeviceStatusRow

// #region InstallationDeviceStatusData
interface InstallationDeviceStatusDataProps {
  installationId: number
  placeHolderUi: string
}

const InstallationDeviceStatusData: React.FC<InstallationDeviceStatusDataProps> = ({ installationId, placeHolderUi }) => {
  const { statusMap, isLoading } = useInstallationStatusContext()
  const installationStatus = statusMap.get(installationId)
  const states = installationStatus?.deviceLinksStatus
  const { t } = useCustomTranslation()

  if (!isLoading && states === undefined) return t(placeHolderUi)

  return (
    <InstallationDeviceStatusContent
      isOffline={states?.offline !== 'ok'}
      hasPv={!!states?.pvState}
      hasPvError={states?.pvState === 'error'}
      isGenerating={states?.pvGenerating === 'generating'}
      batteryStateOfCharge={states?.batSoc ?? null}
      hasBatteryError={states?.batState === 'error'}
      hasGridError={states?.gridState === 'error'}
      hasWallbox={!!states?.wallbox}
      hasWallboxError={states?.wallbox === 'error'}
      isDataUnavailable={!installationStatus}
      isDataLoading={isLoading}
    />
  )
}
// #endregion InstallationDeviceStatusData

// #region InstallationDeviceStatusContent
interface InstallationDeviceStatusContentProps {
  isOffline: boolean
  hasPv: boolean
  hasPvError: boolean
  isGenerating: boolean
  batteryStateOfCharge: number | null
  hasBatteryError: boolean
  hasGridError: boolean
  hasWallbox: boolean
  hasWallboxError: boolean
  isDataUnavailable?: boolean
  isDataLoading?: boolean
}

export const InstallationDeviceStatusContent: React.FC<InstallationDeviceStatusContentProps> = ({
  isOffline,
  hasPv,
  hasPvError,
  isGenerating,
  batteryStateOfCharge,
  hasBatteryError,
  hasGridError,
  hasWallbox,
  hasWallboxError,
  isDataUnavailable = false,
  isDataLoading = false,
}) => {
  if (isDataLoading) return <TextSkeleton variant="body1" sx={{ width: 100 }} />

  if (isOffline) {
    return (
      <StatusIconRow>
        <OfflineStatusIcon isVisible />
      </StatusIconRow>
    )
  }

  return (
    <StatusIconRow>
      <PvStatusIcon hasPv={hasPv} hasPvError={hasPvError} isGenerating={isGenerating} />
      <BatteryStatusIcon stateOfCharge={batteryStateOfCharge ?? undefined} hasError={hasBatteryError} hide={isDataUnavailable} />
      <GridStatusIcon isVisible={hasGridError} />
      <WallboxStatusIcon hasWallbox={hasWallbox} hasError={hasWallboxError} />
    </StatusIconRow>
  )
}

const StatusIconRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{children}</Box>
)
// #endregion InstallationDeviceStatusContent
